import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BarChart3, CreditCard, Receipt, Settings,
  ChevronLeft, ChevronRight, LogOut, Menu, X, Sparkles,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import ClearPathLogo from './ClearPathLogo';
import { useAuth } from '../contexts/AuthContext';
import { DOORS, doorIsOpen } from '../utils/navMap';
import type { DoorPath } from '../utils/navMap';

// Keyed by door path: a label can be reworded without the icon falling off,
// and a new door does not compile until it has an icon.
const DOOR_ICONS: Record<DoorPath, LucideIcon> = {
  '/home': Sparkles, '/sales': BarChart3, '/payments': CreditCard, '/costs': Receipt, '/settings': Settings,
};

// Five doors, one unlabeled section. The rooms behind Sales and Settings are
// tabs on those pages (SectionTabs), so the sidebar stays five lines long.
const NAV_SECTIONS: { label: string | null; items: { label: string; path: string; icon: LucideIcon }[] }[] = [
  {
    label: null,
    items: DOORS.map((d) => ({ ...d, icon: DOOR_ICONS[d.path] })),
  },
];

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/auth');
  };

  const handleNav = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const initials = user?.name
    ? user.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : '??';

  const isCancelled = user?.subscription_status === 'cancelled';

  const planBadge = () => {
    if (isCancelled) {
      return (
        <span className="text-xs font-sans px-2 py-0.5 rounded-full bg-[#B54A4A]/20 text-[#B54A4A] border border-[#B54A4A]/30">
          Cancelled
        </span>
      );
    }
    if (user?.plan === 'pro') {
      return (
        <span className="text-xs font-sans px-2 py-0.5 rounded-full bg-[#2E6B4F] text-white">
          Pro
        </span>
      );
    }
    return (
      <span className="text-xs font-sans px-2 py-0.5 rounded-full bg-white/10 text-white/70 border border-white/20">
        Free
      </span>
    );
  };

  const sidebarContent = (isMobile: boolean) => (
    <>
      {/* Top: logo + nav. Scrolls on short viewports so the account block below never spills out of the rail. */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [mask-image:linear-gradient(to_bottom,black_calc(100%-28px),transparent)]">
        <div className={`p-4 flex items-center ${collapsed && !isMobile ? 'justify-center' : 'justify-between'}`}>
          <ClearPathLogo variant="white" size={collapsed && !isMobile ? 'sm' : 'md'} />
          {isMobile && (
            <button onClick={() => setMobileOpen(false)} aria-label="Close menu" className="-mr-2 w-11 h-11 inline-flex items-center justify-center rounded-[8px] text-white/70 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80">
              <X size={20} strokeWidth={1.5} />
            </button>
          )}
        </div>

        {/* Nav */}
        <nav aria-label="Main" className="mt-2 px-2 pb-8 flex flex-col gap-0.5">
          {NAV_SECTIONS.map((section, si) => (
            <React.Fragment key={section.label || `section-${si}`}>
              {section.label && (!collapsed || isMobile) && (
                <div className="text-[10px] font-sans uppercase tracking-widest text-white/70 px-3 pt-3 pb-1">
                  {section.label}
                </div>
              )}
              {section.items.map((item) => {
                // A door stays lit on every room behind it (/sales/time, /settings/data…).
                const active = doorIsOpen(item.path, location.pathname);
                const Icon = item.icon;
                return (
                  <button
                    key={item.path}
                    onClick={() => handleNav(item.path)}
                    aria-current={active ? 'page' : undefined}
                    aria-label={collapsed && !isMobile ? item.label : undefined}
                    title={collapsed && !isMobile ? item.label : undefined}
                    className={`flex items-center gap-3 px-3 py-2 max-md:min-h-[44px] rounded-[8px] transition-all cursor-pointer font-sans text-sm w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 ${
                      active
                        ? 'text-white bg-white/15 font-medium'
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <Icon size={20} strokeWidth={1.5} className="flex-shrink-0" />
                    {(!collapsed || isMobile) && <span>{item.label}</span>}
                  </button>
                );
              })}
            </React.Fragment>
          ))}
        </nav>
      </div>

      {/* Bottom — always pinned */}
      <div className="flex-shrink-0 px-2 pt-2 pb-2 flex flex-col gap-1 border-t border-white/10">
        {/* Plan badge */}
        {(!collapsed || isMobile) && (
          <div className="px-3 pb-1">
            {planBadge()}
          </div>
        )}

        {/* User info */}
        {(!collapsed || isMobile) && user && (
          <div className="flex items-center gap-2.5 px-3 py-2">
            {user.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-full flex-shrink-0" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                {initials}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-white text-sm font-medium truncate">{user.name}</div>
              <div className="text-white/70 text-xs truncate">{user.email}</div>
            </div>
          </div>
        )}

        {/* Logout */}
        <button
          onClick={handleLogout}
          aria-label={collapsed && !isMobile ? 'Log out' : undefined}
          title={collapsed && !isMobile ? 'Log out' : undefined}
          className="flex items-center gap-3 px-3 py-2 max-md:min-h-[44px] rounded-[8px] text-white/70 hover:text-white hover:bg-white/10 transition-all cursor-pointer font-sans text-sm w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
        >
          <LogOut size={18} strokeWidth={1.5} className="flex-shrink-0" />
          {(!collapsed || isMobile) && <span>Log out</span>}
        </button>

        {/* Collapse toggle - desktop only */}
        {!isMobile && (
          <button
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="flex items-center justify-center py-2 rounded-[8px] text-white/60 hover:text-white transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
          >
            {collapsed ? <ChevronRight size={18} strokeWidth={1.5} /> : <ChevronLeft size={18} strokeWidth={1.5} />}
          </button>
        )}
      </div>
    </>
  );

  return (
    <>
      {/* Mobile hamburger */}
      <button
        onClick={() => setMobileOpen(true)}
        aria-label="Open menu"
        className="fixed top-4 left-4 z-50 md:hidden w-11 h-11 inline-flex items-center justify-center bg-[#4A2D5E] text-white rounded-[8px] shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4A2D5E] focus-visible:ring-offset-2 focus-visible:ring-offset-[#FAF9F7]"
      >
        <Menu size={20} strokeWidth={1.5} />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 md:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Mobile sidebar drawer */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#4A2D5E] flex flex-col justify-between transition-transform duration-300 md:hidden ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {sidebarContent(true)}
      </aside>

      {/* Desktop sidebar */}
      <aside
        className="hidden md:flex sticky top-0 h-screen bg-[#4A2D5E] flex-col justify-between transition-all duration-300 flex-shrink-0"
        style={{ width: collapsed ? 64 : 240 }}
      >
        {sidebarContent(false)}
      </aside>
    </>
  );
}
